import { Button, cn, getPageHeader, PageContainer, PageHeader, PageHeaderActionPrimary, PageHeaderActions, PageHeaderLeading, PageHeaderStack, Skeleton } from '@yeshuman/sdk'
import { LayoutGrid, Loader2, Plus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'


import { useCreateSlideDeck, useSlideDecks, type SlideDeckListItem } from './use-slides'

function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function DeckRow({ deck, onOpen }: { deck: SlideDeckListItem; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full items-center gap-3 py-3 text-left',
        'transition-colors hover:bg-muted/50',
      )}
    >
      <LayoutGrid className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
        {deck.title.trim() || 'Untitled deck'}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {deck.status === 'published' ? 'Published' : 'Draft'}
        {' · '}
        {deck.slide_count} {deck.slide_count === 1 ? 'slide' : 'slides'}
        {' · '}
        {formatDate(deck.updated_at)}
      </span>
    </button>
  )
}

export function SlidesPage() {
  const navigate = useNavigate()
  const header = getPageHeader('/slides', 'Slides', 'Agent-assisted decks with a live HTML preview.')
  const { data: decks = [], isLoading, isError, refetch } = useSlideDecks()
  const createDeck = useCreateSlideDeck()

  async function handleCreate() {
    const deck = await createDeck.mutateAsync('')
    navigate(`/slides/${deck.id}`)
  }

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          title={header.title}
          description={header.description}
          leading={<PageHeaderLeading />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={() => void handleCreate()} disabled={createDeck.isPending}>
                  {createDeck.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  New deck
                </PageHeaderActionPrimary>
              }
            />
          }
        />
      </PageHeaderStack>

      {isLoading ? (
        <div className="flex flex-col gap-3 py-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-sm text-muted-foreground">Could not load slides.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : decks.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-muted">
            <LayoutGrid className="size-6 text-muted-foreground" />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-foreground">No decks yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Create a deck, write HTML on each slide, and publish a public link.
            </p>
          </div>
          <Button onClick={() => void handleCreate()} disabled={createDeck.isPending}>
            <Plus data-icon="inline-start" />
            New deck
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {decks.map((deck) => (
            <DeckRow key={deck.id} deck={deck} onOpen={() => navigate(`/slides/${deck.id}`)} />
          ))}
        </div>
      )}
    </PageContainer>
  )
}
